import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate } from '@/utils/formatters';
import {
  FileText, CreditCard, ChevronUp, ChevronDown,
} from 'lucide-react';

export const FinancialsTab = ({ transactions, llcId }) => {
  const [checkSort, setCheckSort] = useState({ field: 'date', dir: 'desc' });
  const [checkRegisterExpanded, setCheckRegisterExpanded] = useState(() => {
    const saved = localStorage.getItem('checkRegisterExpanded');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleCheckRegister = () => {
    setCheckRegisterExpanded(prev => {
      const newState = !prev;
      localStorage.setItem('checkRegisterExpanded', JSON.stringify(newState));
      return newState;
    });
  };

  // Balance sheet category types
  const balanceSheetTypes = ['current_asset', 'fixed_asset', 'other_asset', 'current_liability', 'long_term_liability', 'equity'];
  const incomeStatementTypes = ['income', 'expense'];
  const getYear = (dateStr) => parseInt(dateStr.split('-')[0]);
  const currentYear = new Date().getFullYear();

  const balanceSheetYears = transactions
    .filter(t => balanceSheetTypes.includes(t.category_type))
    .map(t => getYear(t.date));
  const earliestBalanceYear = balanceSheetYears.length > 0 ? Math.min(...balanceSheetYears) : null;

  const yearsWithIncomeData = new Set(
    transactions.filter(t => incomeStatementTypes.includes(t.category_type)).map(t => getYear(t.date))
  );

  const reportYears = [2025, 2026, 2027, 2028, 2029, 2030];

  const ReportRow = ({ title, checkHasData }) => (
    <div className="flex items-center gap-4 py-1.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2 min-w-[160px]">
        <FileText className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-slate-700 text-sm">{title}</span>
      </div>
      <div className="flex gap-1">
        {reportYears.map(year => {
          const hasData = checkHasData(year);
          const reportTypeParam = title === 'Balance Sheet' ? 'balance' : (title === 'Income Statement' ? 'income' : 'ledger');
          return hasData ? (
            <Link key={year} to={`/reports?llc=${llcId}&type=${reportTypeParam}&year=${year}`}>
              <Button variant="outline" size="sm" className="w-12 h-7 text-xs !bg-amber-100 !border-amber-400 hover:!bg-amber-200 font-semibold text-amber-900 px-0">
                {year}
              </Button>
            </Link>
          ) : (
            <Button key={year} variant="outline" size="sm" className="w-12 h-7 text-xs opacity-30 cursor-not-allowed px-0" disabled>
              {year}
            </Button>
          );
        })}
      </div>
    </div>
  );

  // Check register logic
  const checkTransactions = transactions.filter(t => t.reference && /^\d+$/.test(t.reference.trim()));

  const sortedChecks = [...checkTransactions].sort((a, b) => {
    let aVal, bVal;
    switch (checkSort.field) {
      case 'date': aVal = new Date(a.date); bVal = new Date(b.date); break;
      case 'check': aVal = parseInt(a.reference) || 0; bVal = parseInt(b.reference) || 0; break;
      case 'payee': aVal = (a.vendor_name || '').toLowerCase(); bVal = (b.vendor_name || '').toLowerCase(); break;
      case 'category': aVal = (a.category_name || '').toLowerCase(); bVal = (b.category_name || '').toLowerCase(); break;
      case 'amount': aVal = Math.abs(a.amount); bVal = Math.abs(b.amount); break;
      default: aVal = a[checkSort.field] || ''; bVal = b[checkSort.field] || '';
    }
    if (aVal < bVal) return checkSort.dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return checkSort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleCheckSort = (field) => {
    setCheckSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ field }) => (
    checkSort.field === field ? (
      <span className="text-amber-400 ml-1">{checkSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>
    ) : null
  );

  return (
    <TabsContent value="financials" className="mt-4">
      <Card className="border-slate-200">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="font-serif text-lg">Financial Reports</CardTitle>
          <CardDescription className="text-sm">Generate and export financial statements</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          <div>
            <ReportRow
              title="Balance Sheet"
              checkHasData={(year) => earliestBalanceYear !== null && year >= earliestBalanceYear && year <= currentYear}
            />
            <ReportRow
              title="Income Statement"
              checkHasData={(year) => yearsWithIncomeData.has(year) && year <= currentYear}
            />
            <ReportRow
              title="General Ledger"
              checkHasData={(year) => transactions.some(t => getYear(t.date) === year) && year <= currentYear}
            />
          </div>
        </CardContent>
      </Card>

      {/* Check Register */}
      <Card className="border-slate-200 mt-4">
        <CardHeader className="pb-2">
          <button
            onClick={toggleCheckRegister}
            className="w-full flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors -m-2"
          >
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              <CardTitle className="font-serif">Check Register</CardTitle>
              <span className="text-xs text-slate-500 bg-amber-100 px-2 py-0.5 rounded-full">
                {checkTransactions.length}
              </span>
            </div>
            {checkRegisterExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-500" />
            )}
          </button>
          {checkRegisterExpanded && (
            <CardDescription className="mt-2">All checks written from this LLC account</CardDescription>
          )}
        </CardHeader>
        {checkRegisterExpanded && (
          <CardContent>
            {checkTransactions.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No checks recorded yet</p>
                <p className="text-sm mt-1">Checks are identified by numeric Reference #</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-[350px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800 sticky top-0 z-10">
                      <tr>
                        <th className="text-left text-white font-semibold px-4 py-3 cursor-pointer hover:bg-slate-700" onClick={() => handleCheckSort('date')}>
                          Date<SortIcon field="date" />
                        </th>
                        <th className="text-left text-white font-semibold px-4 py-3 cursor-pointer hover:bg-slate-700" onClick={() => handleCheckSort('check')}>
                          Check #<SortIcon field="check" />
                        </th>
                        <th className="text-left text-white font-semibold px-4 py-3 cursor-pointer hover:bg-slate-700" onClick={() => handleCheckSort('payee')}>
                          Payee<SortIcon field="payee" />
                        </th>
                        <th className="text-left text-white font-semibold px-4 py-3 cursor-pointer hover:bg-slate-700" onClick={() => handleCheckSort('category')}>
                          Category<SortIcon field="category" />
                        </th>
                        <th className="text-left text-white font-semibold px-4 py-3 cursor-pointer hover:bg-slate-700" onClick={() => handleCheckSort('amount')}>
                          Amount<SortIcon field="amount" />
                        </th>
                        <th className="text-left text-white font-semibold px-4 py-3">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedChecks.map(check => (
                        <tr key={check.id} className="hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                          <td className="font-mono text-sm py-2 px-4">{formatDate(check.date)}</td>
                          <td className="font-mono font-medium py-2 px-4">{check.reference}</td>
                          <td className="py-2 px-4">{check.vendor_name || '-'}</td>
                          <td className="text-sm text-slate-600 py-2 px-4">{check.category_name || '-'}</td>
                          <td className="text-right font-mono text-rose-600 py-2 px-4">
                            -${Math.abs(check.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="text-sm text-slate-500 py-2 px-4">{check.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </TabsContent>
  );
};
